import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { Layout } from "../../../shared/components/Layout";
import { Context } from "../Context";
import { useAdditionalInformationStep } from "../config/additionalInformation";
import { useBusinessProfileStep } from "../config/businessProfile";
import { useCertifyTaxPayerInfoStep } from "../config/certifyTaxPayerInfo";
import { useCreateAccountStep } from "../config/createAccount";
import { useEligibilityRequirementsStep } from "../config/eligibilityRequirements";
import { usePaymentMethodStep } from "../config/paymentMethod";
import { usePayoutMethodStep } from "../config/payoutMethod";
import { usePayrollFundingMethodStep } from "../config/payrollFundingMethod";
import { usePayrollSettingsStep } from "../config/payrollSettings";
import { useTaxWithholdingsStep } from "../config/taxWithholdings";
import { useEmployeeTaxFormsStep } from "../../EmployeeTaxForms/config/employeeTaxFormsStepConfig";
import { OnboardingContext, OnboardingModule } from "../types";
import { OnboardingModuleType } from "./OnboardingFlowUtils";
import { FlowSwitch } from "../../../shared/components/FlowSwitch";
import { FlowStep } from "../../../shared/components/FlowSwitch/types";

type OnboardingStepBuilders = {
  [K in Exclude<OnboardingModuleType, "entity_change">]: (
    module: Extract<OnboardingModule, { type: K }>,
    ctx: OnboardingContext
  ) => FlowStep | null;
};

type Props = {
  basePath: string;
  modules: OnboardingModule[];
  onBack?: () => void;
  onSuccess?: () => void;
  isLoading?: boolean;
  hideHeader?: boolean;
};

export const OnboardingFlow: React.FC<Props> = ({
  basePath,
  modules,
  onBack,
  onSuccess,
  isLoading,
  hideHeader
}) => {
  const location = useLocation();

  const buildCreateAccount = useCreateAccountStep(modules);
  const buildPayrollFundingMethod = usePayrollFundingMethodStep(modules);
  const buildPayrollSettings = usePayrollSettingsStep(modules);
  const buildAdditionalInformation = useAdditionalInformationStep(modules);
  const buildBusinessProfile = useBusinessProfileStep(modules);
  const buildCertifyTaxPayerInfo = useCertifyTaxPayerInfoStep(modules);
  const buildPayoutMethod = usePayoutMethodStep(modules);
  const buildPaymentMethod = usePaymentMethodStep(modules);
  const buildEligibilityRequirements = useEligibilityRequirementsStep(modules);
  const buildTaxWithholdings = useTaxWithholdingsStep(modules);
  const buildEmployeeTaxForms = useEmployeeTaxFormsStep(modules);

  const [contextValue, setContextValue] = useState<OnboardingContext>({});

  const steps = useMemo(() => {
    const stepBuilders: OnboardingStepBuilders = {
      create_account: buildCreateAccount,
      payroll_funding_method: buildPayrollFundingMethod,
      payroll_settings: buildPayrollSettings,
      additional_information: buildAdditionalInformation,
      business_profile: buildBusinessProfile,
      certify_tax_payer_info: buildCertifyTaxPayerInfo,
      payout_method: buildPayoutMethod,
      payment_method: buildPaymentMethod,
      eligibility_requirements: buildEligibilityRequirements,
      tax_withholdings: buildTaxWithholdings,
      employee_tax_forms: buildEmployeeTaxForms
    };
    return modules
      .map(onboardingModule => {
        const builder =
          stepBuilders[onboardingModule.type as keyof OnboardingStepBuilders];
        if (!builder) {
          throw new Error(
            `No step config found for module type: ${onboardingModule.type}`
          );
        }
        return (
          builder as (
            module: OnboardingModule,
            ctx: OnboardingContext
          ) => FlowStep | null
        )(onboardingModule, contextValue);
      })
      .filter(step => step !== null) as FlowStep[];
  }, [
    modules,
    contextValue,
    buildCreateAccount,
    buildPayrollFundingMethod,
    buildPayrollSettings,
    buildAdditionalInformation,
    buildBusinessProfile,
    buildCertifyTaxPayerInfo,
    buildPayoutMethod,
    buildPaymentMethod,
    buildEligibilityRequirements,
    buildTaxWithholdings,
    buildEmployeeTaxForms
  ]);

  const currentStep = useMemo(() => {
    // Prefer the active sub-step so the header shows the specific step's title
    // (e.g. the tax form name like "Federal Form W-4") rather than the parent
    // step's generic title (e.g. "Tax form requirement"). Falls back to the
    // top-level step on its landing route.
    for (const step of steps) {
      const matchedSubStep = step.steps?.find(subStep =>
        location.pathname.includes(`${step.slug}/${subStep.slug}`)
      );
      if (matchedSubStep) {
        return matchedSubStep;
      }
    }
    return steps.find(step => location.pathname.includes(step.slug));
  }, [location.pathname, steps]);

  if (!steps.length || isLoading) {
    return null;
  }

  return (
    <Context.Provider
      value={{
        value: contextValue,
        setValue: setContextValue,
        modules
      }}
    >
      <Layout
        hideHeader={hideHeader}
        navigation={steps.length > 1 ? steps : undefined}
        title={currentStep?.title}
        // Wide-form steps (e.g. the EmployeeTaxForms wizard, whose canvas is
        // 944px) flag themselves via FlowStep.wideContent; every other step
        // keeps the Layout's default column widths.
        wideContent={currentStep?.wideContent}
        onClose={onBack}
      >
        <FlowSwitch
          path={basePath}
          slug={basePath}
          steps={steps}
          title="Onboarding"
          onBack={onBack}
          onNext={onSuccess}
        />
      </Layout>
    </Context.Provider>
  );
};
